/**
 * AffiliateDashboard.tsx
 *
 * Dashboard для партнёров affiliate программы
 *
 * Показывает:
 * - Статус affiliate (pending/active/rejected/banned)
 * - Affiliate код и pretty URL для sharing
 * - Статистику: referrals, conversions, earnings, pending payout
 * - Список referrals
 * - Список commissions
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../api';
import './AffiliateDashboard.css';

interface AffiliateDashboard {
  affiliate: {
    id: number;
    user_id: number;
    affiliate_code: string;
    status: string;
    commission_rate: number;
    payment_email: string;
    total_earnings: number;
    pending_payout: number;
    total_referrals: number;
    total_conversions: number;
    created_at: string;
  };
  referrals: Array<{
    id: number;
    referred_user_id: number;
    affiliate_code: string;
    converted: boolean;
    conversion_count: number;
    total_revenue: number;
    created_at: string;
  }>;
  commissions: Array<{
    id: number;
    commission_type: string;
    amount: number;
    commission_rate: number;
    status: string;
    created_at: string;
    approved_at: string | null;
    paid_at: string | null;
  }>;
}

const AffiliateDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<AffiliateDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiRequest('/affiliates/dashboard', {
        method: 'GET'
      });

      setDashboard(data.data);
    } catch (err: any) {
      if (err.status === 404) {
        setError('У вас нет affiliate аккаунта. Подайте заявку на партнёрство.');
      } else {
        setError(err.message || 'Ошибка загрузки dashboard');
      }
      console.error('Error loading affiliate dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadDashboard();
    }
  }, [user]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const getPrettyUrl = (code: string) => {
    return `${window.location.origin}/go/${code.toLowerCase()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMoney = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };

  const getStatusDisplay = (status: string) => {
    const statusMap: Record<string, { label: string; className: string; icon: string }> = {
      pending: { label: 'Ожидает одобрения', className: 'status-pending', icon: '⏳' },
      active: { label: 'Активен', className: 'status-active', icon: '✅' },
      rejected: { label: 'Отклонён', className: 'status-rejected', icon: '❌' },
      banned: { label: 'Заблокирован', className: 'status-banned', icon: '🚫' }
    };
    return statusMap[status] || { label: status, className: 'status-unknown', icon: '❓' };
  };

  const getCommissionStatusDisplay = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      pending: { label: 'Ожидает одобрения', className: 'commission-pending' },
      approved: { label: 'Одобрена', className: 'commission-approved' },
      paid: { label: 'Выплачена', className: 'commission-paid' },
      rejected: { label: 'Отклонена', className: 'commission-rejected' }
    };
    return statusMap[status] || { label: status, className: 'commission-unknown' };
  };

  const getCommissionTypeDisplay = (type: string) => {
    const typeMap: Record<string, string> = {
      first_payment: 'Первый платёж',
      recurring: 'Ежемесячный',
      bonus: 'Бонус'
    };
    return typeMap[type] || type;
  };

  if (!user) {
    return (
      <div className="affiliate-dashboard-container">
        <div className="auth-required">
          <h2>Войдите в аккаунт</h2>
          <p>Для просмотра affiliate dashboard необходимо войти в аккаунт</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="affiliate-dashboard-container">
        <div className="loading">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p>Загрузка dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="affiliate-dashboard-container">
        <div className="error-message">
          <h2>❌ Ошибка</h2>
          <p>{error}</p>
          {error.includes('Подайте заявку') && (
            <p className="mt-3">
              <a href="/affiliate/apply" className="btn btn-primary">
                Подать заявку на партнёрство
              </a>
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return null;
  }

  const affiliate = dashboard.affiliate;
  const statusDisplay = getStatusDisplay(affiliate.status);

  return (
    <div className="affiliate-dashboard-container">
      {/* Header */}
      <div className="affiliate-header">
        <h1>🎯 Партнёрский Dashboard</h1>
        <div className={`affiliate-status ${statusDisplay.className}`}>
          {statusDisplay.icon} {statusDisplay.label}
        </div>
      </div>

      {/* Pending status warning */}
      {affiliate.status === 'pending' && (
        <div className="alert alert-warning">
          ⏳ Ваша заявка находится на рассмотрении. После одобрения вы сможете начать приглашать людей и получать комиссию.
        </div>
      )}

      {/* Affiliate Links Section */}
      {affiliate.status === 'active' && (
        <div className="affiliate-section">
          <h2>📎 Ваша партнёрская ссылка</h2>
          <div className="affiliate-links">
            <div className="link-item">
              <label>Pretty URL (рекомендуется):</label>
              <div className="link-copy-group">
                <input
                  type="text"
                  value={getPrettyUrl(affiliate.affiliate_code)}
                  readOnly
                  className="link-input"
                />
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(getPrettyUrl(affiliate.affiliate_code), 'Pretty URL')}
                >
                  {copySuccess === 'Pretty URL' ? '✅ Скопировано' : '📋 Копировать'}
                </button>
              </div>
            </div>

            <div className="link-item">
              <label>Ваш код:</label>
              <div className="affiliate-code-display">
                <code>{affiliate.affiliate_code}</code>
                <button
                  className="copy-btn-small"
                  onClick={() => copyToClipboard(affiliate.affiliate_code, 'Code')}
                >
                  {copySuccess === 'Code' ? '✅' : '📋'}
                </button>
              </div>
            </div>
          </div>

          <div className="sharing-tips">
            <h4>💡 Как делиться ссылкой:</h4>
            <ul>
              <li>Публикуйте pretty URL в соцсетях, блогах, YouTube</li>
              <li>Добавьте ссылку в описание канала Telegram</li>
              <li>Отправляйте друзьям в личных сообщениях</li>
              <li>Указывайте в email рассылках</li>
            </ul>
          </div>
        </div>
      )}

      {/* Stats Section */}
      <div className="affiliate-section">
        <h2>📊 Статистика</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-value">{affiliate.total_referrals}</div>
            <div className="stat-label">Всего переходов</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-value">{affiliate.total_conversions}</div>
            <div className="stat-label">Оплаченных пользователей</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💵</div>
            <div className="stat-value">{formatMoney(affiliate.total_earnings)}</div>
            <div className="stat-label">Всего заработано</div>
          </div>

          <div className="stat-card highlight">
            <div className="stat-icon">⏳</div>
            <div className="stat-value">{formatMoney(affiliate.pending_payout)}</div>
            <div className="stat-label">Ожидает выплаты</div>
          </div>
        </div>

        <div className="commission-info">
          <p>
            <strong>Ваша комиссия:</strong> {affiliate.commission_rate}% от всех платежей приведённых пользователей
          </p>
          <p>
            <strong>Email для выплат:</strong> {affiliate.payment_email}
          </p>
        </div>
      </div>

      {/* Referrals Section */}
      {dashboard.referrals.length > 0 && (
        <div className="affiliate-section">
          <h2>👥 Приведённые пользователи ({dashboard.referrals.length})</h2>
          <div className="referrals-table">
            <table className="table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Дата регистрации</th>
                  <th>Статус</th>
                  <th>Платежей</th>
                  <th>Принесено</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.referrals.map((referral) => (
                  <tr key={referral.id}>
                    <td>
                      <code>#{referral.referred_user_id}</code>
                    </td>
                    <td>
                      {formatDate(referral.created_at)}
                    </td>
                    <td>
                      <span className={`badge ${referral.converted ? 'badge-success' : 'badge-secondary'}`}>
                        {referral.converted ? '✅ Оплатил' : '⏳ Не оплатил'}
                      </span>
                    </td>
                    <td>{referral.conversion_count}</td>
                    <td><strong>{formatMoney(referral.total_revenue)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commissions Section */}
      {dashboard.commissions.length > 0 && (
        <div className="affiliate-section">
          <h2>💸 Комиссии ({dashboard.commissions.length})</h2>
          <div className="commissions-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Тип</th>
                  <th>Сумма</th>
                  <th>Ставка</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.commissions.map((commission) => {
                  const statusDisplay = getCommissionStatusDisplay(commission.status);
                  return (
                    <tr key={commission.id}>
                      <td>{formatDate(commission.created_at)}</td>
                      <td>{getCommissionTypeDisplay(commission.commission_type)}</td>
                      <td><strong>{formatMoney(commission.amount)}</strong></td>
                      <td>{commission.commission_rate}%</td>
                      <td>
                        <span className={`badge ${statusDisplay.className}`}>
                          {statusDisplay.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {affiliate.status === 'active' && dashboard.referrals.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🎯</div>
          <h3>Начните приглашать людей!</h3>
          <p>Поделитесь своей партнёрской ссылкой и получайте {affiliate.commission_rate}% от каждого платежа.</p>
        </div>
      )}

      {/* Help Section */}
      <div className="affiliate-section help-section">
        <h2>❓ Нужна помощь?</h2>
        <p>
          По всем вопросам пишите в{' '}
          <a href="https://t.me/AlexGo007" target="_blank" rel="noreferrer">
            Telegram: @AlexGo007
          </a>
        </p>
        <p className="text-muted small">
          Выплаты производятся вручную, после одобрения комиссии.
          Обычно это занимает 3-5 рабочих дней.
        </p>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
